import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { ApiTokenInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getAdSpaceApiTokensColumns } from './ApiTokensColumns';
import { AdSpaceApiTokensUpdateDialog } from './ApiTokensUpdateDialog';

const AdSpaceApiTokensAdminList: React.FC = () => {
    const [tokens, setTokens] = useState<ApiTokenInterface[]>([]);
    const [token, setToken] = useState<ApiTokenInterface | null>(null);
    const [tokenId, setTokenId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [tokenToDelete, setTokenToDelete] = useState<ApiTokenInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [tokenToUpdate, setTokenToUpdate] = useState<ApiTokenInterface | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Fetch all tokens
    const handleGetAllTokens = async () => {
        setError(null);
        setToken(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<ApiTokenInterface[]>>('/apiTokens');
            if (result.status && result.data) {
                setTokens(result.data);
            } else {
                setError(result.message || 'Failed to fetch API tokens');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch API tokens');
        }
    };

    // Fetch token by id
    const handleGetTokenById = async () => {
        setError(null);
        setTokens([]);
        setSuccess(null);
        if (!tokenId) return setError('Please enter an API Token ID');
        try {
            const result = await apiGet<GenericResponseInterface<ApiTokenInterface>>(`/apiTokens/${tokenId}`);
            if (result.status && result.data) {
                setToken(result.data);
            } else {
                setError(result.message || 'Failed to fetch API token');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch API token');
        }
    };

    // Delete token
    const handleDeleteToken = async () => {
        if (!tokenToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/apiTokens/delete/${tokenToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'API token deleted successfully');
                setDeleteDialogOpen(false);
                setTokenToDelete(null);
                handleGetAllTokens();
            } else {
                setError(result.message || 'Failed to delete API token');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete API token');
        }
    };

    // Update token
    const handleUpdateToken = async () => {
        if (!tokenToUpdate || !tokenToUpdate.id) return;
        setUpdateError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface<ApiTokenInterface>>(
                `/apiTokens/update/${tokenToUpdate.id}`,
                'PUT',
                tokenToUpdate
            );
            if (result.status) {
                setSuccess(result.message || 'API token updated successfully');
                setUpdateDialogOpen(false);
                setTokenToUpdate(null);
                handleGetAllTokens();
            } else {
                setUpdateError(result.message || 'Failed to update API token');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update API token');
        }
    };

    const columns = getAdSpaceApiTokensColumns(
        setTokenToDelete,
        setDeleteDialogOpen,
        setTokenToUpdate,
        setUpdateDialogOpen
    );

    useEffect(() => {
        handleGetAllTokens();
    }, []);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAllTokens}>Get All API Tokens</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetTokenById}>Get Token by ID</Button>
                <TextField
                    label="API Token ID"
                    value={tokenId}
                    onChange={e => setTokenId(e.target.value)}
                    size="small"
                />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {tokens.length > 0 && (
                    <DataGrid
                        rows={tokens.map(a => ({ ...a, id: a.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {token && (
                    <DataGrid
                        rows={[{ ...token, id: token.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this API token?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeleteToken}>Delete</Button>
                </DialogActions>
            </Dialog>
            <AdSpaceApiTokensUpdateDialog
                open={updateDialogOpen}
                tokenToUpdate={tokenToUpdate}
                setTokenToUpdate={setTokenToUpdate}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setSuccess(null);
                }}
                onSave={handleUpdateToken}
                updateError={updateError}
                updateSuccess={success}
            />
        </Box>
    );
};

export default AdSpaceApiTokensAdminList;